import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SnackbarProvider } from "notistack";
import { Spin } from "antd";
import SignIn from "./pages/SignIn";
import Dashboard from "./pages/Dashboard";
import UserManagement from "./pages/UserManagement";
import PlanManagement from "./pages/PlanManagement";
import CustomerManagement from "./pages/CustomerManagement";
import CustomerHistory from "./pages/CustomerHistory";
import CustomerWizard from "./components/CustomerWizard";
import SubscriptionManagement from "./pages/SubscriptionManagement";
import PaymentHistory from "./pages/PaymentHistory";
import FeatureManagement from "./pages/FeatureManagement";
import PlanFeaturesManagement from "./pages/PlanFeaturesManagement";
import Layout from "./components/Layout";
import AppErrorBoundary from "./components/AppErrorBoundary";
import { useAuth } from "./context/AuthContext";
import { canAccessRoute, defaultRouteFor } from "./constants/permissions";
import CustomThemeProvider from "./context/ThemeContext";
import { trackPageView } from "./observability/telemetry";
// Keycloak disabled for now.
// import { rememberPostLoginRedirect } from "./services/keycloakSession";
import "./index.css";

const LoadingState = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "grid",
      placeItems: "center",
    }}
  >
    <Spin size="large" />
  </div>
);

const RequireAuth = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated, isInitialized } = useAuth();

  if (!isInitialized) {
    return <LoadingState />;
  }

  if (!isAuthenticated) {
    // Keycloak disabled for now.
    // rememberPostLoginRedirect(`${location.pathname}${location.search}${location.hash}`);
    sessionStorage.setItem(
      "admin.login.redirect_to",
      `${location.pathname}${location.search}${location.hash}`
    );
    return <Navigate to="/signin" replace />;
  }

  return children;
};

const RequireRole = ({ routeKey, children }) => {
  const { user } = useAuth();

  if (!canAccessRoute(user, routeKey)) {
    return <Navigate to={defaultRouteFor(user)} replace />;
  }

  return children;
};

const ProtectedRoute = ({ routeKey, children }) => (
  <RequireAuth>
    <RequireRole routeKey={routeKey}>
      <Layout>{children}</Layout>
    </RequireRole>
  </RequireAuth>
);

const TelemetryRouteTracker = () => {
  const location = useLocation();

  React.useEffect(() => {
    trackPageView(document.title || "Virtuevise Admin Portal", `${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
};

const App = () => {
  return (
    <CustomThemeProvider>
      <AppErrorBoundary>
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          autoHideDuration={3000}
          dense
          preventDuplicate
          iconVariant={{
            success: "✓",
            error: "✕",
            warning: "⚠",
            info: "ℹ",
          }}
        >
          <Router>
            <TelemetryRouteTracker />
            <Routes>
              <Route path="/signin" element={<SignIn />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute routeKey="/dashboard">
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute routeKey="/users">
                    <UserManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/plans"
                element={
                  <ProtectedRoute routeKey="/plans">
                    <PlanManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers"
                element={
                  <ProtectedRoute routeKey="/customers">
                    <CustomerManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/:id/history"
                element={
                  <ProtectedRoute routeKey="/customers/history">
                    <CustomerHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/add"
                element={
                  <ProtectedRoute routeKey="/customers/add">
                    <CustomerWizard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/edit/:id"
                element={
                  <ProtectedRoute routeKey="/customers/edit">
                    <CustomerWizard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscriptions"
                element={
                  <ProtectedRoute routeKey="/subscriptions">
                    <SubscriptionManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payments"
                element={
                  <ProtectedRoute routeKey="/payments">
                    <PaymentHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/features"
                element={
                  <ProtectedRoute routeKey="/features">
                    <FeatureManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/plan-features"
                element={
                  <ProtectedRoute routeKey="/plan-features">
                    <PlanFeaturesManagement />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/signin" replace />} />
            </Routes>
          </Router>
        </SnackbarProvider>
      </AppErrorBoundary>
    </CustomThemeProvider>
  );
};

export default App;
